import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { MessageCircle, Phone, Plus, Search, SlidersHorizontal, Sparkles, Upload, X } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { formatDate, formatDateTime, dueLabel } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useMasterList } from "@/hooks/useMasterData";
import { LEAD_SCOPES } from "@/lib/leadScopes";
import TonalBadge from "@/components/TonalBadge";

function truncate(s, n = 60) {
  if (!s) return s;
  return s.length > n ? `${s.slice(0, n).trimEnd()}…` : s;
}

function fmtBudget(min, max, unit) {
  const hasMin = min != null && min !== "" && Number(min) > 0;
  const hasMax = max != null && max !== "" && Number(max) > 0;
  if (!hasMin && !hasMax) return null;
  const u = unit ? ` ${unit}` : "";
  const n = (v) => Number(v).toLocaleString("en-IN");
  if (hasMin && hasMax) return `₹${n(min)}${u} – ₹${n(max)}${u}`;
  return `₹${n(hasMin ? min : max)}${u}`;
}

export default function LeadsList({ scope = "individual" }) {
  const cfg = LEAD_SCOPES[scope] || LEAD_SCOPES.individual;
  const isClient = cfg.key === "institutional";
  const navigate = useNavigate();
  const { user } = useAuth();
  const [statusId, setStatusId] = useState("all");
  const [sourceId, setSourceId] = useState("all");
  const [warmthId, setWarmthId] = useState("all");
  const [search, setSearch] = useState("");

  const { data: statuses = [] } = useMasterList("lead_status");
  const { data: sources = [] } = useMasterList("lead_source");
  const { data: warmth = [] } = useMasterList("lead_warmth");
  const { data: priceUnits = [] } = useMasterList("price_unit");
  const { data: cities = [] } = useMasterList("city");
  const { data: nobList = [] } = useMasterList("nature_of_business");

  const unitLabel = useMemo(() => Object.fromEntries(priceUnits.map((d) => [d.id, d.label])), [priceUnits]);
  const cityLabel = useMemo(() => Object.fromEntries(cities.map((d) => [d.id, d.label])), [cities]);
  const nobLabel = useMemo(() => Object.fromEntries(nobList.map((d) => [d.id, d.label])), [nobList]);

  const { data: leads = [], isLoading } = useQuery({
    queryKey: ["leads", cfg.clientType, statusId, sourceId, warmthId, search],
    queryFn: async () => {
      const params = { client_type: cfg.clientType };
      if (statusId !== "all") params.status_id = statusId;
      if (sourceId !== "all") params.source_id = sourceId;
      if (warmthId !== "all") params.warmth_id = warmthId;
      if (search) params.q = search;
      const { data } = await api.get("/leads", { params });
      return data;
    },
  });

  const leadIds = useMemo(() => leads.map((l) => l.id), [leads]);
  const { data: summary = {} } = useQuery({
    queryKey: ["leads-summary", leadIds],
    queryFn: async () => {
      const { data } = await api.post("/leads/list-summary", { lead_ids: leadIds });
      return data;
    },
    enabled: leadIds.length > 0,
    keepPreviousData: true,
  });

  return (
    <div className="p-8">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow">{cfg.eyebrow}</div>
          <h1 className="font-display text-3xl font-bold text-slate-900 mt-1" data-testid="leads-title">{cfg.title}</h1>
          <p className="text-sm text-slate-600 mt-1.5">
            {user?.roles?.includes("admin") ? cfg.adminSubtitle : cfg.ownSubtitle}
          </p>
        </div>
        <Button
          className="bg-[#1C1F26] hover:bg-[#2E3138] text-white"
          onClick={() => navigate(cfg.addPath)}
          data-testid="add-lead-button"
        >
          <Plus className="h-4 w-4 mr-1.5" /> {cfg.addLabel}
        </Button>
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-md p-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search by name, code, contact, company…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="leads-search-input"
          />
        </div>
        <SlidersHorizontal className="h-4 w-4 text-slate-500" />
        <Select value={statusId} onValueChange={setStatusId}>
          <SelectTrigger className="w-[170px]" data-testid="leads-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            {statuses.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={sourceId} onValueChange={setSourceId}>
          <SelectTrigger className="w-[150px]" data-testid="leads-source-filter"><SelectValue placeholder="Source" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sources</SelectItem>
            {sources.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={warmthId} onValueChange={setWarmthId}>
          <SelectTrigger className="w-[120px]" data-testid="leads-warmth-filter"><SelectValue placeholder="Warmth" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Warmth</SelectItem>
            {warmth.map((w) => <SelectItem key={w.id} value={w.id}>{w.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="text-xs text-slate-500 font-mono-num ml-auto">
          {leads.length} {leads.length === 1 ? cfg.noun : `${cfg.noun}s`}
        </div>
      </div>

      <div className="mt-4 bg-white border border-slate-200 rounded-md">
        <div className="overflow-auto max-h-[calc(100vh-190px)] rounded-md">
          <table className="data-table w-full text-left">
            <thead className="sticky top-0 z-20">
              <tr className="border-b border-slate-200">
                <th className="label-eyebrow !text-slate-600">{cfg.firstColHeader}</th>
                <th className="label-eyebrow !text-slate-600">Budget</th>
                <th className="label-eyebrow !text-slate-600">Action</th>
                <th className="label-eyebrow !text-slate-600">Lead Quality</th>
                <th className="label-eyebrow !text-slate-600">Lead Warmth</th>
                <th className="label-eyebrow !text-slate-600">Lead Status</th>
                <th className="label-eyebrow !text-slate-600">Next Contact Date</th>
                <th className="label-eyebrow !text-slate-600">Comments</th>
                <th className="label-eyebrow !text-slate-600">Assets Offered</th>
                <th className="label-eyebrow !text-slate-600">Top Matches</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i}><td colSpan={10} className="!py-3"><Skeleton className="h-8 w-full" /></td></tr>
                ))
              ) : leads.length === 0 ? (
                <tr>
                  <td colSpan={10} className="py-16 text-center">
                    {(statusId !== "all" || sourceId !== "all" || warmthId !== "all" || search) ? (
                      <div className="text-sm text-slate-500" data-testid="leads-filtered-empty">
                        No {cfg.noun}s match your filters.
                      </div>
                    ) : (
                      <>
                        <div className="font-display text-lg text-slate-700">No {cfg.noun}s yet</div>
                        <div className="text-sm text-slate-500 mt-1">
                          {isClient ? "Add an institutional client to get started." : "Add a lead manually, or import a batch to get started."}
                        </div>
                        <div className="mt-4 flex items-center justify-center gap-2">
                          <Button
                            className="bg-slate-900 hover:bg-slate-800"
                            onClick={() => navigate(cfg.addPath)}
                            data-testid="empty-state-add-lead"
                          >
                            <Plus className="h-4 w-4 mr-1.5" /> {cfg.addLabel}
                          </Button>
                          {!isClient && (
                            <Button
                              variant="outline"
                              onClick={() => navigate("/bulk-import")}
                              data-testid="empty-state-import-leads"
                            >
                              <Upload className="h-4 w-4 mr-1.5" /> Import via Bulk Upload
                            </Button>
                          )}
                        </div>
                      </>
                    )}
                  </td>
                </tr>
              ) : (
                leads.map((l) => {
                  const nc = dueLabel(l.next_contact_date);
                  const s = summary[l.id] || {};
                  const phoneDigits = (l.contact_no || "").replace(/\D/g, "");
                  const budget = fmtBudget(l.budget_min, l.budget_max, unitLabel[l.budget_unit_id]);
                  return (
                    <tr key={l.id} className="border-b border-slate-100 cursor-pointer" onClick={() => navigate(`${cfg.basePath}/${l.id}`)} data-testid={`lead-row-${l.id}`}>
                      {/* Client & Company */}
                      <td>
                        <div className="font-mono-num text-[11px] text-slate-500">{l.lead_code}</div>
                        <Link to={`${cfg.basePath}/${l.id}`} className="text-sm font-semibold text-slate-900 hover:underline" onClick={(e) => e.stopPropagation()}>
                          {l.lead_name}
                        </Link>
                        {isClient ? (
                          (l.nature_of_business_id || l.nature_of_business_other) && (
                            <div className="text-xs text-slate-500">{nobLabel[l.nature_of_business_id] === "Other" ? (l.nature_of_business_other || "Other") : (nobLabel[l.nature_of_business_id] || "—")}</div>
                          )
                        ) : (
                          l.company_name && <div className="text-xs text-slate-500">{l.company_name}</div>
                        )}
                      </td>
                      {/* Budget */}
                      <td className="text-sm font-mono-num text-slate-700">{budget || <span className="text-slate-300">—</span>}</td>
                      {/* Action */}
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1.5">
                          <a
                            href={`tel:${l.contact_no}`}
                            title="Call"
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 text-slate-700 hover:bg-slate-50"
                            data-testid={`lead-call-${l.id}`}
                          >
                            <Phone className="h-3.5 w-3.5" />
                          </a>
                          <a
                            href={`https://wa.me/${phoneDigits}`}
                            target="_blank"
                            rel="noreferrer"
                            title="WhatsApp"
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                            data-testid={`lead-whatsapp-${l.id}`}
                          >
                            <MessageCircle className="h-3.5 w-3.5" />
                          </a>
                        </div>
                      </td>
                      {/* Lead Quality */}
                      <td>{l.lead_quality_id ? <TonalBadge listType="lead_quality" id={l.lead_quality_id} /> : <span className="text-slate-300">—</span>}</td>
                      {/* Lead Warmth */}
                      <td>{l.lead_warmth_id ? <TonalBadge listType="lead_warmth" id={l.lead_warmth_id} /> : <span className="text-slate-300">—</span>}</td>
                      {/* Lead Status */}
                      <td>{l.lead_status_id ? <TonalBadge listType="lead_status" id={l.lead_status_id} /> : <span className="text-slate-300">—</span>}</td>
                      {/* Next Contact Date */}
                      <td className="text-sm">
                        {l.next_contact_date ? (
                          <div>
                            <div className="font-mono-num text-xs">{formatDate(l.next_contact_date)}</div>
                            {nc && (
                              <div className={`text-[10px] font-semibold mt-0.5 ${nc.tone === "critical" ? "text-red-700" : nc.tone === "warning" ? "text-amber-700" : "text-slate-500"}`}>
                                {nc.text}
                              </div>
                            )}
                          </div>
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                      {/* Comments */}
                      <td onClick={(e) => e.stopPropagation()}>
                        <CommentCell leadId={l.id} latest={s.latest_comment} count={s.comment_count || 0} />
                      </td>
                      {/* Assets Offered */}
                      <td onClick={(e) => e.stopPropagation()}>
                        <AssetsOfferedCell entries={s.assets_offered} />
                      </td>
                      {/* Top Matches */}
                      <td onClick={(e) => e.stopPropagation()}>
                        <TopMatchesCell leadId={l.id} cityLabel={cityLabel} />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function scoreTone(score) {
  if (score >= 75) return "text-emerald-700";
  if (score >= 50) return "text-amber-700";
  return "text-slate-600";
}

function PopHeader({ title, onClose, testid }) {
  return (
    <div className="px-3 py-2 border-b border-slate-100 flex items-center justify-between">
      <div className="label-eyebrow !text-slate-600">{title}</div>
      <button
        type="button"
        onClick={onClose}
        className="text-slate-400 hover:text-slate-700"
        data-testid={testid}
        aria-label="Close"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function AssetsOfferedCell({ entries }) {
  const [open, setOpen] = useState(false);
  const list = entries || [];
  if (list.length === 0) {
    return <span className="text-slate-300" data-testid="lead-assets-offered-empty">—</span>;
  }
  const visible = list.length > 4 ? list.slice(0, 3) : list;
  const overflow = list.length > 4 ? list.length - 3 : 0;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div
          className="flex flex-wrap items-center gap-1 max-w-[220px] cursor-pointer"
          data-testid="lead-assets-offered-cell"
        >
          {visible.map((e) => (
            <span key={e.id} className="inline-flex items-center max-w-[120px] px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 border border-slate-200 text-slate-700">
              <span className="truncate">{e.name}</span>
            </span>
          ))}
          {overflow > 0 && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-slate-900 text-white" data-testid="lead-assets-offered-overflow">
              +{overflow}
            </span>
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-0" data-testid="lead-assets-offered-popover">
        <PopHeader title={`Assets Offered (${list.length})`} onClose={() => setOpen(false)} testid="lead-assets-offered-close" />
        <div className="max-h-72 overflow-y-auto divide-y divide-slate-100">
          {list.map((e) => (
            <Link
              key={e.id}
              to={`/assets/${e.asset_id}`}
              className="block px-3 py-2 hover:bg-slate-50"
            >
              <div className="text-sm font-medium text-slate-900 truncate">{e.name}</div>
              <div className="font-mono-num text-[11px] text-slate-500">{e.asset_code}</div>
            </Link>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function CommentCell({ leadId, latest, count }) {
  const [open, setOpen] = useState(false);
  const { data: history = [], isLoading } = useQuery({
    queryKey: ["lead-comments", leadId],
    queryFn: async () => (await api.get(`/leads/${leadId}/comments`)).data,
    enabled: open,
    staleTime: 30_000,
  });
  if (!latest) {
    return <span className="text-slate-300" data-testid="lead-comment-empty">—</span>;
  }
  // History endpoint returns newest-first; show ascending (oldest first) in the popup.
  const ascending = [...history].reverse();
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div
          className="max-w-[200px] cursor-pointer"
          data-testid="lead-comment-cell"
        >
          <div className="text-sm text-slate-700">{truncate(latest.comment, 60)}</div>
          <div className="text-[10px] text-slate-400 font-mono-num mt-0.5">
            {formatDateTime(latest.logged_at)}
            {count > 1 && <span className="ml-1 text-slate-500 font-semibold">· {count}</span>}
          </div>
        </div>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-0" data-testid="lead-comment-popover">
        <PopHeader title={`Comment History (${count})`} onClose={() => setOpen(false)} testid="lead-comment-close" />
        <div className="max-h-80 overflow-y-auto divide-y divide-slate-100">
          {isLoading ? (
            <div className="px-3 py-3"><Skeleton className="h-12 w-full" /></div>
          ) : (
            ascending.map((c) => (
              <div key={c.id} className="px-3 py-2">
                <div className="text-sm text-slate-800 whitespace-pre-line">{c.comment}</div>
                <div className="text-[10px] text-slate-400 font-mono-num mt-1">
                  {formatDateTime(c.logged_at)}
                  {c.logged_by_name && <span className="ml-1 text-slate-500">· {c.logged_by_name}</span>}
                </div>
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function TopMatchesCell({ leadId, cityLabel }) {
  const [open, setOpen] = useState(false);
  const { data, isFetching } = useQuery({
    queryKey: ["lead-top-matches", leadId],
    queryFn: async () => (await api.get(`/leads/${leadId}/matching-assets`)).data,
    enabled: open, // computed only when the trigger is tapped/clicked
    staleTime: 60_000,
  });
  const matches = data?.matches || [];
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex h-8 items-center gap-1.5 px-2.5 rounded-md border border-[#B8935A]/40 text-[#8a6a39] hover:bg-[#B8935A]/10 text-xs font-semibold"
          data-testid="lead-top-matches-trigger"
        >
          <Sparkles className="h-3.5 w-3.5" /> Top 5
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="lead-top-matches-popover">
        <PopHeader title="Top 5 Matching Assets" onClose={() => setOpen(false)} testid="lead-top-matches-close" />
        <div className="max-h-80 overflow-y-auto divide-y divide-slate-100">
          {isFetching ? (
            <div className="px-3 py-3 space-y-2"><Skeleton className="h-8 w-full" /><Skeleton className="h-8 w-full" /></div>
          ) : matches.length === 0 ? (
            <div className="px-3 py-4 text-sm text-slate-500" data-testid="lead-top-matches-empty">No active matching assets found.</div>
          ) : (
            matches.map((m) => (
              <Link key={m.id} to={`/assets/${m.id}`} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50">
                <span className={`font-mono-num text-sm font-bold w-11 shrink-0 ${scoreTone(m.match_score || 0)}`}>
                  {Math.round(m.match_score || 0)}%
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">{m.name}</div>
                  <div className="text-[11px] text-slate-500">{cityLabel[m.city_id] || "—"}</div>
                </div>
              </Link>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

